import { useState } from 'react';
import { Alert, Button, Group, Paper, Stack, Stepper, Text, Textarea, TextInput, Title, Tooltip } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import type { StockAdjustmentWrite } from '@zebra/core';
import { apiFetch, ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { SiteSelect } from '../../../features/stores-transfers/components/SiteSelect';
import { LineRowsEditor, emptyLineRow, type LineFormRow } from '../../../features/stores-transfers/components/LineRowsEditor';
import { LedgerPreview } from '../../../features/stores-transfers/components/LedgerPreview';
import { useCreateAdjustment } from '../../../features/stores-transfers/api';

/** Owned by G-A (Wave HOWICK-1). Guided "create an ad-hoc stock adjustment" flow
 * (BUILD_SPEC.md §8.6): site &amp; reason -> lines (signed qty per product×location)
 * -> review with a ledger-effect preview -> Post. Mirrors issues.new.tsx's shape —
 * the one difference is a line's qty may be NEGATIVE (removes stock) as well as
 * positive (adds stock), so "valid" here is "non-zero", not "> 0". */
export const Route = createFileRoute('/_authenticated/stores-transfers/adjustments/new')({
  component: NewAdjustmentPage,
});

function NewAdjustmentPage() {
  const canOperate = useCanOperate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createAdjustment = useCreateAdjustment();

  const [active, setActive] = useState(0);
  const [siteId, setSiteId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineFormRow[]>([emptyLineRow()]);
  const [postError, setPostError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const detailsValid = Boolean(siteId && reason.trim());
  const linesValid = lines.length > 0 && lines.every((l) => l.productId && l.locationId && l.qty && Number(l.qty) !== 0);

  function buildPayload(): StockAdjustmentWrite {
    return {
      siteId,
      reason: reason.trim(),
      notes: notes.trim() || null,
      lines: lines.map((l) => ({ productId: l.productId, locationId: l.locationId, qtyDelta: l.qty })),
    };
  }

  async function handleSaveDraft() {
    setPostError(null);
    try {
      const created = await createAdjustment.mutateAsync(buildPayload());
      void navigate({ to: '/stores-transfers/adjustments/$adjustmentId', params: { adjustmentId: created.id } });
    } catch {
      // `createAdjustment.isError`/`.error` already drive the red text below.
    }
  }

  async function handlePost() {
    setPostError(null);
    setPosting(true);
    try {
      const created = await createAdjustment.mutateAsync(buildPayload());
      try {
        await apiFetch(`/stores/adjustments/${created.id}/post`, { method: 'POST' });
        await queryClient.invalidateQueries({ queryKey: ['stores', 'adjustments'] });
      } catch (err) {
        // The DRAFT was created successfully even though the immediate Post failed
        // (e.g. a transient network error) — send the user to the adjustment's own
        // detail page, where DRAFT documents show a Post button so they can retry,
        // rather than losing the draft they just entered.
        const message = err instanceof ApiRequestError ? err.message : 'Posting failed unexpectedly.';
        setPostError(`Draft ${created.number} was saved, but posting failed: ${message} You can retry from the adjustment.`);
      }
      void navigate({ to: '/stores-transfers/adjustments/$adjustmentId', params: { adjustmentId: created.id } });
    } catch {
      // Creation itself failed (e.g. validation) — `createAdjustment.isError`/
      // `.error` already drive the red text below.
    } finally {
      setPosting(false);
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Stack gap={2}>
        <Title order={2}>New stock adjustment</Title>
        <Text c="dimmed" size="sm">
          Correct a stock quantity directly — for write-offs or found stock, not a physical movement. Moving stock
          between locations is a Transfer; reconciling a full count is a Stocktake (Silo &amp; Counts).
        </Text>
      </Stack>

      {postError && (
        <Alert color="amber" icon={<IconAlertTriangle size={18} />} title="Partial success">
          {postError}
        </Alert>
      )}

      <Stepper active={active} onStepClick={setActive}>
        <Stepper.Step label="Details" description="Site &amp; reason">
          <Paper withBorder radius="md" p="md" mt="md">
            <Stack gap="md">
              <SiteSelect label="Site" value={siteId} onChange={setSiteId} required />
              <TextInput
                label="Reason"
                placeholder="e.g. Damaged stock write-off, cycle-count correction…"
                value={reason}
                onChange={(event) => setReason(event.currentTarget.value)}
                required
              />
              <Textarea label="Notes" value={notes} onChange={(event) => setNotes(event.currentTarget.value)} autosize minRows={2} />
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Lines" description="What to adjust">
          <Paper withBorder radius="md" p="md" mt="md">
            <Text size="sm" c="dimmed" mb="sm">
              Enter a positive quantity to add stock, or a negative quantity (e.g. -10.000) to remove it.
            </Text>
            <LineRowsEditor rows={lines} onChange={setLines} siteId={siteId || undefined} includeLocation locationLabel="Location" />
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Review" description="Ledger effect &amp; Post">
          <Paper withBorder radius="md" p="md" mt="md">
            <LedgerPreview siteId={siteId || undefined} lines={lines} attribution={`Reason: ${reason}`} />
          </Paper>
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between">
        <Button variant="default" onClick={() => setActive((s) => Math.max(0, s - 1))} disabled={active === 0}>
          Back
        </Button>
        {active < 2 ? (
          <Button
            onClick={() => setActive((s) => Math.min(2, s + 1))}
            disabled={(active === 0 && !detailsValid) || (active === 1 && !linesValid)}
          >
            Continue
          </Button>
        ) : (
          <Group>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button variant="default" disabled={!canOperate} onClick={handleSaveDraft} loading={createAdjustment.isPending && !posting}>
                  Save as draft
                </Button>
              </span>
            </Tooltip>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button onClick={handlePost} loading={posting} disabled={!canOperate || !detailsValid || !linesValid}>
                  Post
                </Button>
              </span>
            </Tooltip>
          </Group>
        )}
      </Group>
      {(createAdjustment.isError && !postError) && (
        <Text c="red" size="sm">
          {createAdjustment.error instanceof Error ? createAdjustment.error.message : 'Something went wrong creating the adjustment.'}
        </Text>
      )}
    </Stack>
  );
}
